import React from 'react';
import HFinput from '../../../components/common/input/HFinput';
import HFinputPhone from '../../../components/common/input/HFinputPhone';
import HFbutton from '../../../components/common/input/HFbutton';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../store/hook';
import instance from '../../../api/axios/utils.ts/instance';
import {
  closeModal,
  openAlert,
  openModal,
} from '../../../store/features/modalSlice';
import { ApiResponse } from '../../../constants/types';
import { useNavigate } from 'react-router-dom';
import TextWinds from '../../../components/common/TextWinds';
import { handleCopyClipBoard } from '../../../api/utils/util-func';
import { timeLeftCheck, timeLeftToString } from '../../../controllers/useTimer';
import useSmsHook from '../../../controllers/useSmsHook';

type findIdByPhoneDTO = {
  name: string;
  verifySmsId: string;
  phone: string;
  verifyType: string;
};

const LoginIdPhone = () => {
  const dispatch = useAppDispatch();
  const navigete = useNavigate();
  const [verifyNumber, setVerifyNumber] = React.useState<string>('');
  const [isConfirmPhone, setIsConfirmPhone] = React.useState<boolean>(false);
  const [isConfirmCode, setIsConfirmCode] = React.useState<boolean>(false);
  const [isPossibleNext, setIsPossibleNext] = React.useState<boolean>(false);

  const { sendVerifyNumberWithName, checkVerifyNumber, timeLeft } =
    useSmsHook();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<findIdByPhoneDTO>();

  const name = watch('name');

  const openAlertWithMessage = (msg: string) => {
    dispatch(openAlert({ message: msg }));
  };

  const onSubmit = (data: findIdByPhoneDTO) => {
    data.verifyType = 'PHONE';

    if (data.verifySmsId === undefined) {
      dispatch(openAlert({ message: '전화번호 인증을 받아주세요.' }));
      return;
    }

    const params: findIdByPhoneDTO = {
      name: data.name,
      phone: data.phone.replace(/-/g, ''),
      verifyType: 'PHONE',
      verifySmsId: data.verifySmsId,
    };

    postUserId(params);
  };

  // 인증번호 발송
  const sendSmsHandler = () => {
    setIsConfirmPhone(true);

    // 이름 유효성 검사
    const regName = /^[가-힣]{2,}$/;
    if (!regName.test(name)) {
      dispatch(
        openAlert({
          message: '이름은 공백 없이 한글 2글자 이상 입력해주세요.',
        }),
      );
      setIsConfirmPhone(false);
      return;
    }

    // 휴대폰 번호 유효성 검사
    const regPhone = /^0(?:2|[0-9]{2})(?:[0-9]{3,4})[0-9]{4}$/;
    if (!regPhone.test(watch('phone').replace(/-/g, ''))) {
      dispatch(
        openAlert({
          message: '휴대폰 번호는 숫자 01012345678 형식으로 입력해주세요.',
        }),
      );
      setIsConfirmPhone(false);
      return;
    }

    sendVerifyNumberWithName(watch('phone'), name).then((msg) => {
      setIsConfirmPhone(false);
      openAlertWithMessage(msg);
    });
  };

  // 인증번호 확인
  const confirmSmsHandler = () => {
    setIsConfirmCode(true);

    checkVerifyNumber(watch('phone'), verifyNumber).then((res) => {
      if (res.confirm) {
        setIsConfirmPhone(true);
        setIsConfirmCode(true);
        setIsPossibleNext(true);
        setValue('verifySmsId', res.confirm.toString());
      } else {
        setIsConfirmCode(false);
      }
      openAlertWithMessage(res.msg);
    });
  };

  const postUserId = async (params: findIdByPhoneDTO) => {
    try {
      const res = await instance.post<ApiResponse<{ uid: string }[]>>(
        '/v1/mypage/user/find-uid',
        params,
      );

      dispatch(
        openModal({
          body: (
            <ViewIdModal
              ids={res.data.data.map((item) => item.uid)}
              linkTo={(link) => navigete(link)}
            />
          ),
        }),
      );
    } catch (err) {
      dispatch(openAlert({ message: '알 수 없는 오류가 발생했습니다.' }));
    }
  };

  return (
    <form>
      <TextWinds
        type="caption1"
        className="mb-5 rounded bg-gray2 p-2 text-center"
      >
        학생 아이디는 클래스 선생님의 LMS를 통해 찾으실 수 있습니다.
      </TextWinds>
      <HFinput
        label="이름"
        name="name"
        register={register('name', {
          required: { value: true, message: '이름을 입력해주세요.' },
        })}
        placeholder="이름을 입력해주세요."
        error={errors.name?.message}
        disabled={timeLeftCheck(timeLeft) || isConfirmCode}
      />
      <div className="relative">
        <HFinputPhone
          label="휴대폰 번호"
          placeholder="휴대폰 번호를 입력해주세요."
          name="phone"
          register={register('phone', {
            required: { value: true, message: '휴대폰 번호를 입력해주세요.' },
          })}
          disabled={timeLeftCheck(timeLeft) || isConfirmCode}
        />
        <div className="absolute right-1 top-8">
          <HFbutton
            height={40}
            onClick={sendSmsHandler}
            disabled={timeLeftCheck(timeLeft) || isConfirmPhone}
          >
            {timeLeftCheck(timeLeft) ? timeLeftToString(timeLeft) : '인증번호'}
          </HFbutton>
        </div>
      </div>
      <div className="relative">
        <HFinput
          label="인증번호"
          type="text"
          placeholder="인증번호를 입력해주세요."
          value={verifyNumber}
          onChange={(e) => setVerifyNumber(e.target.value)}
          disabled={isConfirmCode}
        />
        <div className="absolute right-1 top-8">
          <HFbutton
            height={40}
            onClick={confirmSmsHandler}
            disabled={isConfirmCode}
          >
            {isConfirmCode ? '인증완료' : '인증확인'}
          </HFbutton>
        </div>
      </div>
      <div className="flex gap-3">
        <HFbutton type="Outlined" onClick={() => navigete('/signin')}>
          취소
        </HFbutton>
        <HFbutton onClick={handleSubmit(onSubmit)} disabled={!isPossibleNext}>
          다음
        </HFbutton>
      </div>
    </form>
  );
};

const ViewIdModal = ({
  ids,
  linkTo,
}: {
  ids: string[];
  linkTo: (link: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const closeHandler = () => {
    dispatch(closeModal());
    linkTo('/signin');
  };

  const movePageHandler = () => {
    dispatch(closeModal());
    linkTo(`/signin/password`);
  };

  const copyHandler = (id: string) => {
    handleCopyClipBoard(id).then(() => {
      alert('복사되었습니다.');
    });
  };

  return (
    <div className="w-full max-w-[480px] rounded bg-white p-5">
      <TextWinds type="title_h1" className="text-center">
        아이디 찾기 완료
      </TextWinds>
      <div className="flex flex-col gap-2 p-5">
        {ids.map((item, index) => (
          <div
            className="relative flex items-center gap-2 rounded p-3 text-center text-lg shadow-card"
            key={index}
          >
            <div className="absolute right-1 top-1">
              <HFbutton height={40} onClick={() => copyHandler(item)}>
                복사하기
              </HFbutton>
            </div>
            <div>{item}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <HFbutton type="Outlined" onClick={closeHandler}>
          닫기
        </HFbutton>
        <HFbutton onClick={movePageHandler}>비밀번호 찾기</HFbutton>
      </div>
    </div>
  );
};
export default LoginIdPhone;
